import { AlertCircle } from "lucide-react";

/**
 * LegalPage
 * - Backwards compatible: sections can use { h, p } (single paragraph)
 * - Rich: sections can use { h, blocks: [ { type: "p", text }, { type: "ul", items } ] }
 * - Pass isDraft={false} for documents that have been reviewed and finalised.
 */
const renderBlock = (block, idx) => {
  if (block.type === "ul") {
    return (
      <ul key={idx} className="mt-3 space-y-2 list-disc pl-6 text-[#4B6B86] leading-relaxed">
        {block.items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }
  if (block.type === "link") {
    return (
      <p key={idx} className="mt-3 text-[#4B6B86] leading-relaxed">
        {block.label}
        <a href={block.href} className="text-[#1677C8] hover:text-[#14324A] font-medium transition-colors break-all">{block.text}</a>
      </p>
    );
  }
  return (
    <p key={idx} className="mt-3 text-[#4B6B86] leading-relaxed">
      {block.text}
    </p>
  );
};

const LegalPage = ({ title, kicker, sections, isDraft = false, effectiveDate, lead, lastUpdated }) => (
  <>
    <section className="container-page pt-16 md:pt-24 pb-8">
      <div className="max-w-3xl">
        <span className="eyebrow">{kicker}</span>
        <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">{title}</h1>
        {effectiveDate && (
          <p className="mt-4 text-sm text-[#4B6B86]"><span className="font-semibold text-[#14324A]">Effective date:</span> {effectiveDate}</p>
        )}
        {lead && <p className="mt-5 text-[#4B6B86] leading-relaxed text-lg">{lead}</p>}
      </div>
    </section>
    <section className="container-page py-4">
      {isDraft && (
        <div className="rounded-3xl border border-[#FFD45C] bg-[#FFFDF7] p-6 mb-8 flex gap-3">
          <AlertCircle size={20} className="text-[#14324A] shrink-0 mt-0.5" />
          <p className="text-sm text-[#4B6B86] leading-relaxed">
            <strong className="text-[#14324A]">Draft template.</strong> This document is a placeholder that must be reviewed and approved by an Australian legal or compliance professional before publication. Do not treat this content as legal advice.
          </p>
        </div>
      )}
      <div className="card-soft p-8 md:p-12 space-y-8">
        {sections.map((s, idx) => (
          <div key={s.h + idx}>
            <h2 className="font-display font-bold text-xl md:text-2xl text-[#14324A]">{s.h}</h2>
            {s.p && <p className="mt-3 text-[#4B6B86] leading-relaxed">{s.p}</p>}
            {s.blocks && s.blocks.map(renderBlock)}
          </div>
        ))}
        {lastUpdated && (
          <div className="pt-6 mt-6 border-t border-[#E5F0FA]" data-testid="legal-last-updated">
            <p className="text-xs text-[#4B6B86]">
              <span className="font-semibold text-[#14324A]">Last updated:</span> {lastUpdated}
            </p>
          </div>
        )}
      </div>
    </section>
  </>
);

export default LegalPage;
